import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useQueueCounts } from '@/hooks/use-queue-counts';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/state/store/auth';
import { getUserScope } from '@/lib/user-scope';
import { searchEmployees } from '@/modules/shared/hr-lookups';
import { useTranslateRuleErrors } from '@/modules/time-attendance/attendance/hooks/use-attendance';
import { AttendanceRuleError } from '@/modules/time-attendance/attendance/services/attendance-rules';
import {
  bulkReviewAttendanceCorrections,
  getAttendanceCorrections,
  getAttendanceRoster,
  getAttendances,
  importAttendance,
  reviewAttendanceCorrection,
  waiveCorrectionFine,
} from '../services/manage-attendance.service';
import {
  GetAttendanceCorrectionsParams,
  GetAttendancesParams,
} from '../types/manage-attendance.types';

/** The signed-in user as recorded in `ActionTakenBy` when they decide a correction. */
export const useCurrentReviewer = () => {
  const user = useAuthStore((state) => state.user);
  // Memoised: callers list it as a hook dependency, and a new object per render would rebuild the
  // table columns (and the self-review check) every render.
  return useMemo(
    () => ({
      itemId: user?.itemId ?? '',
      name: user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '',
    }),
    [user]
  );
};

export const useGetAttendances = (params: GetAttendancesParams, enabled = true) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-attendances', params],
    queryFn: () => getAttendances(params),
    enabled,
    staleTime: 60 * 1000,
  });
};

/** One organization day for every employee in scope, with or without a record. */
export const useGetAttendanceRoster = (
  params: Parameters<typeof getAttendanceRoster>[0],
  enabled = true
) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-attendances', 'roster', params],
    queryFn: () => getAttendanceRoster(params),
    enabled,
    staleTime: 60 * 1000,
  });
};

export const useGetAttendanceCorrections = (
  params: GetAttendanceCorrectionsParams,
  enabled = true
) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-attendance-corrections', params],
    queryFn: () => getAttendanceCorrections(params),
    enabled,
    staleTime: 60 * 1000,
  });
};

/** The queues of the Corrections list (Component Map › Queue status), in display order. */
export const CORRECTION_QUEUES = ['Pending', 'Approved', 'Rejected', 'All'] as const;
export type CorrectionQueue = (typeof CORRECTION_QUEUES)[number];
export const correctionQueueFilter = (queue: CorrectionQueue) =>
  queue === 'All' ? {} : { Status: queue };

/** How many corrections each queue holds within `base` (the reviewer's scope). */
export const useCorrectionQueueCounts = (base: Record<string, unknown>, enabled = true) =>
  useQueueCounts({
    queryKey: ['hr-manage-attendance-corrections'],
    base,
    queues: CORRECTION_QUEUES.map((value) => ({ value, filter: correctionQueueFilter(value) })),
    fetchTotal: async (filter) =>
      (
        (await getAttendanceCorrections({ pageNo: 1, pageSize: 1, filter, order: [] })) as {
          AttendanceCorrections?: { totalCount?: number };
        }
      ).AttendanceCorrections?.totalCount ?? 0,
    enabled,
  });

/** The employees the correction list's keyword names (reuses the shared `searchEmployees`); only
 * runs once a keyword is typed. */
export const useSearchCorrectionEmployees = (keyword: string, departmentId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-attendance-correction-employees', keyword, departmentId ?? ''],
    queryFn: () => searchEmployees(keyword, departmentId),
    enabled: keyword.length > 0,
    staleTime: 60 * 1000,
  });
};

/** An employee's department — used on the drill-down history page to check whether the
 * reviewer may see this particular employee's department. */
export const useGetTargetScope = (userId: string | undefined) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-attendance-target-scope', userId],
    queryFn: () => getUserScope(userId as string),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useReviewAttendanceCorrection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const translateRuleErrors = useTranslateRuleErrors();

  return useGlobalMutation({
    mutationFn: (params: Parameters<typeof reviewAttendanceCorrection>[0]) =>
      translateRuleErrors(() => reviewAttendanceCorrection(params)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-manage-attendance-corrections'] });
      queryClient.invalidateQueries({ queryKey: ['hr-manage-attendances'] });
      queryClient.invalidateQueries({ queryKey: ['hr-attendance-days'] });
      toast({ title: t('CORRECTION_REQUEST_UPDATED') });
    },
  });
};

export const useWaiveCorrectionFine = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const translateRuleErrors = useTranslateRuleErrors();

  return useGlobalMutation({
    mutationFn: (itemId: string) => translateRuleErrors(() => waiveCorrectionFine(itemId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-manage-attendance-corrections'] });
      toast({ title: t('FINE_WAIVED') });
    },
  });
};

/** Approves or rejects several requests, each through its workflow; reports how many went through. */
export const useBulkReviewAttendanceCorrections = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: Parameters<typeof bulkReviewAttendanceCorrections>[0]) =>
      bulkReviewAttendanceCorrections(params),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['hr-manage-attendance-corrections'] });
      queryClient.invalidateQueries({ queryKey: ['hr-manage-attendances'] });
      queryClient.invalidateQueries({ queryKey: ['hr-attendance-days'] });
      // Why the first few were refused (the workflow's translation keys, e.g. already reviewed).
      const reasons = [
        ...new Set(
          result.failed.map(({ error }) =>
            error instanceof AttendanceRuleError
              ? t(error.message, {
                  ...error.params,
                  defaultValue: error.defaultMessage ?? error.message,
                })
              : t('ATTENDANCE_CORRECTION_REVIEW_FAILED')
          )
        ),
      ].slice(0, 3);
      toast({
        title: t('BULK_REVIEW_DONE', {
          succeeded: result.succeeded.length,
          failed: result.failed.length,
        }),
        ...(reasons.length > 0
          ? { description: reasons.join(' · '), variant: 'destructive' as const }
          : {}),
      });
    },
  });
};

/** Imports parsed CSV rows; refreshes the lists. The result (saved / skipped rows) is shown by the dialog. */
export const useImportAttendance = () => {
  const queryClient = useQueryClient();
  const translateRuleErrors = useTranslateRuleErrors();

  return useGlobalMutation({
    mutationFn: (params: Parameters<typeof importAttendance>[0]) =>
      translateRuleErrors(() => importAttendance(params)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-manage-attendances'] });
      queryClient.invalidateQueries({ queryKey: ['hr-attendance-days'] });
    },
  });
};
